import React, { useCallback } from 'react';
import { Modal } from 'react-bootstrap';
import {
  FloatingOrder,
  VoidedOrder,
  CompletedOrder,
} from 'utils/posIndexedDBUtils';
import { CloudOff, CheckCircle2 } from 'lucide-react';
import Button from 'components/Button';
import styles from './OrdersListModal.module.scss';

export type OrderType = 'floating' | 'voided' | 'completed';

interface OrdersListModalProps {
  show: boolean;
  onHide: () => void;
  orderType: OrderType;
  orders: FloatingOrder[] | VoidedOrder[] | CompletedOrder[];
  onContinue?: (order: FloatingOrder) => void;
  onVoid?: (order: FloatingOrder | CompletedOrder) => void;
}

function OrdersListModal({
  show,
  onHide,
  orderType,
  orders,
  onContinue,
  onVoid,
}: OrdersListModalProps) {
  const formatCurrency = useCallback((amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }, []);

  const formatDate = useCallback((timestamp: number): string => {
    return new Date(timestamp).toLocaleString();
  }, []);

  const getSyncStatusIcon = (order: VoidedOrder | CompletedOrder) => {
    if (order.syncStatus === 'synced') {
      return <CheckCircle2 size={16} className={styles.syncedIcon} />;
    }
    return <CloudOff size={16} className={styles.pendingIcon} />;
  };

  const getSyncStatusText = (order: VoidedOrder | CompletedOrder) => {
    if (order.syncStatus === 'synced') {
      return 'Synced';
    }
    return 'Pending Sync';
  };

  const getTitle = () => {
    switch (orderType) {
      case 'floating':
        return 'Floating Orders';
      case 'voided':
        return 'Voided Orders';
      case 'completed':
        return 'Completed Orders';
      default:
        return 'Orders';
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>{getTitle()}</Modal.Title>
      </Modal.Header>
      <Modal.Body className={styles.modalBody}>
        {orders.length === 0 ? (
          <div className={styles.emptyState}>No orders found</div>
        ) : (
          <div className={styles.ordersList}>
            {orders.map((order) => {
              if (orderType === 'floating') {
                const floatingOrder = order as FloatingOrder;
                return (
                  <div key={floatingOrder.id} className={styles.orderItem}>
                    <div className={styles.orderHeader}>
                      <div className={styles.orderInfo}>
                        <div className={styles.orderDate}>
                          {formatDate(floatingOrder.createdAt)}
                        </div>
                        <div className={styles.orderCustomer}>
                          {floatingOrder.customerName || 'Customer'}
                        </div>
                      </div>
                      <div className={styles.orderTotal}>
                        {formatCurrency(floatingOrder.subtotal)}
                      </div>
                    </div>
                    <div className={styles.orderItems}>
                      {floatingOrder.cart.length} item(s)
                    </div>
                    <div className={styles.orderActions}>
                      {onContinue && (
                        <Button
                          size="sm"
                          onClick={() => onContinue(floatingOrder)}
                        >
                          Continue
                        </Button>
                      )}
                      {onVoid && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onVoid(floatingOrder)}
                        >
                          Void
                        </Button>
                      )}
                    </div>
                  </div>
                );
              }

              if (orderType === 'voided') {
                const voidedOrder = order as VoidedOrder;
                const total = voidedOrder.requestData.cashOrArEntries.reduce(
                  (sum, entry) => sum + entry.amount,
                  0,
                );
                return (
                  <div key={voidedOrder.id} className={styles.orderItem}>
                    <div className={styles.orderHeader}>
                      <div className={styles.orderInfo}>
                        <div className={styles.orderDate}>
                          {formatDate(voidedOrder.voidedAt)}
                        </div>
                        {voidedOrder.reason && (
                          <div className={styles.orderReason}>
                            Reason: {voidedOrder.reason}
                          </div>
                        )}
                      </div>
                      <div className={styles.orderTotal}>
                        {formatCurrency(total)}
                      </div>
                    </div>
                    <div className={styles.syncStatus}>
                      {getSyncStatusIcon(voidedOrder)}
                      <span>{getSyncStatusText(voidedOrder)}</span>
                    </div>
                  </div>
                );
              }

              if (orderType === 'completed') {
                const completedOrder = order as CompletedOrder;
                return (
                  <div key={completedOrder.id} className={styles.orderItem}>
                    <div className={styles.orderHeader}>
                      <div className={styles.orderInfo}>
                        <div className={styles.orderDate}>
                          {formatDate(completedOrder.completedAt)}
                        </div>
                        <div className={styles.orderCustomer}>
                          {completedOrder.requestData.customerId}
                        </div>
                      </div>
                      <div className={styles.orderTotal}>
                        {formatCurrency(completedOrder.total)}
                      </div>
                    </div>
                    <div className={styles.orderDetails}>
                      <div>Change: {formatCurrency(completedOrder.change)}</div>
                    </div>
                    <div className={styles.syncStatus}>
                      {getSyncStatusIcon(completedOrder)}
                      <span>{getSyncStatusText(completedOrder)}</span>
                    </div>
                    {onVoid && completedOrder.syncStatus === 'synced' && (
                      <div className={styles.orderActions}>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onVoid(completedOrder)}
                        >
                          Void
                        </Button>
                      </div>
                    )}
                  </div>
                );
              }

              return null;
            })}
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
}

export default OrdersListModal;
